import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Clock, Download } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useEnterpriseContext } from '@/hooks/useEnterpriseContext';
import { getUnitAccessLogs } from '@/services/api.client';
import { formatDate, formatDuration, statusBadgeClass } from '@/lib/utils';

const TYPES = [
  { value: 'all', label: 'All types' },
  { value: 'single_visit', label: 'Single visit' },
  { value: 'recurring', label: 'Recurring' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'taxi', label: 'Taxi' },
  { value: 'contractor', label: 'Worker pass' },
];

export function EnterpriseHistoryPage() {
  const ctx = useEnterpriseContext();
  const [search, setSearch] = useState('');
  const [type, setType] = useState('all');
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);
  const pageIndex = cursors.length - 1;
  const cursor = cursors[pageIndex];

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      'enterprise-access-logs',
      ctx?.propertyId,
      ctx?.tenantId,
      {
        visitorName: search || undefined,
        invitationType: type === 'all' ? undefined : type,
        cursor,
      },
    ],
    queryFn: () =>
      ctx
        ? getUnitAccessLogs(ctx.propertyId, ctx.tenantId, {
            ...(cursor ? { cursor } : {}),
            ...(search ? { visitorName: search } : {}),
            ...(type !== 'all' ? { invitationType: type } : {}),
            limit: 25,
          })
        : null,
    enabled: !!ctx,
  });

  const goNext = () => {
    const cursor = data?.meta.nextCursor;
    if (cursor) {
      setCursors((prev) => [...prev, cursor]);
    }
  };

  const goPrev = () => {
    if (pageIndex > 0) {
      setCursors((prev) => prev.slice(0, -1));
    }
  };

  // Client-side CSV export of the current page's visible rows. Useful for
  // small audit asks (e.g. "send me Tuesday's visitors") without needing a
  // server-side export endpoint. We keep it scoped to the current filter
  // so admins can shape the export by adjusting filters first.
  const downloadCsv = (): void => {
    const rows = data?.data ?? [];
    if (rows.length === 0) return;
    const header = ['Visitor', 'Gate', 'Checked In', 'Duration (min)', 'Status'];
    const escape = (v: string | number): string => {
      const s = String(v);
      return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const lines = [
      header.join(','),
      ...rows.map((r) =>
        [
          escape(r.visitorName),
          escape(r.gateName),
          escape(r.checkedInAt),
          escape(r.durationMinutes ?? ''),
          escape(r.status),
        ].join(','),
      ),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `company-visitors-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl">Visitor History</h1>
        <Button variant="outline" size="sm" onClick={downloadCsv} disabled={!data?.data?.length}>
          <Download className="size-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <Input
          placeholder="Search visitor name…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCursors([undefined]);
          }}
          className="max-w-xs"
        />
        <Select
          value={type}
          onValueChange={(v) => {
            setType(v);
            setCursors([undefined]);
          }}
        >
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Access Log</CardTitle>
        </CardHeader>
        <CardContent>
          {isError ? (
            <div
              role="alert"
              className="rounded-xl border border-destructive/40 bg-destructive/5 p-4"
            >
              <p className="text-sm font-medium">Couldn&apos;t load visitor history</p>
              <p className="text-sm text-muted-foreground">Please try again later.</p>
            </div>
          ) : isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : data?.data.length ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Visitor</TableHead>
                    <TableHead>Gate</TableHead>
                    <TableHead>Checked In</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="font-medium">{log.visitorName}</TableCell>
                      <TableCell>{log.gateName}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {log.checkedInAt ? formatDate(log.checkedInAt) : '—'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {log.durationMinutes ? formatDuration(log.durationMinutes) : '—'}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusBadgeClass(log.status)}>{log.status}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between mt-4">
                <Button variant="outline" size="sm" onClick={goPrev} disabled={pageIndex === 0}>
                  Previous
                </Button>
                <Button variant="outline" size="sm" onClick={goNext} disabled={!data.meta.hasMore}>
                  Next
                </Button>
              </div>
            </>
          ) : (
            <div className="py-10 text-center">
              <Clock className="size-8 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No visits recorded.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
